
import { GameHoleBirdieRecord } from '@/integrations/supabase/scores/scoreHistoryService';
import { BirdieRecord, TeeBox } from '@/types';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Edit, Trash2, Loader2 } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface GameBirdieHistoryProps {
  records: GameHoleBirdieRecord[];
  currentUserId: string;
  courseId: string;
  holeNumber: number;
  onEdit: (record: BirdieRecord) => void;
  onDelete: (record: BirdieRecord) => void;
  isLoading?: boolean;
}

/** Converts a GameHoleBirdieRecord to the BirdieRecord shape expected by edit/delete handlers */
function toBirdieRecord(record: GameHoleBirdieRecord, courseId: string, holeNumber: number): BirdieRecord {
  return {
    id: record.id,
    userId: record.userId,
    courseId,
    holeNumber,
    date: record.date,
    teeBox: record.teeBox as TeeBox,
    scoreType: (record.scoreType as BirdieRecord['scoreType']) || 'birdie',
    notes: record.notes || undefined,
    gameIds: [],
  };
}

const scoreTypeLabel: Record<string, string> = {
  birdie: 'Birdie',
  eagle: 'Eagle',
  double_eagle: 'Double Eagle',
};

const GameBirdieHistory = ({
  records,
  currentUserId,
  courseId,
  holeNumber,
  onEdit,
  onDelete,
  isLoading = false,
}: GameBirdieHistoryProps) => {
  const isMobile = useIsMobile();

  return (
    <div className="mt-4">
      <h3 className={`font-medium ${isMobile ? 'text-sm' : ''}`}>Game history for this hole</h3>

      {isLoading ? (
        <div className="flex justify-center items-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-forest" />
        </div>
      ) : records.length === 0 ? (
        <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground mt-2`}>
          No scores recorded yet.
        </p>
      ) : (
        <div className="max-h-60 overflow-y-auto mt-2 border rounded-md">
          {records.map((record) => {
            const isOwn = record.userId === currentUserId || record.enteredByUserId === currentUserId;
            return (
              <div
                key={record.id}
                className="p-3 border-b last:border-b-0 flex justify-between items-center"
              >
                <div className="flex items-center gap-2">
                  <Avatar className={isMobile ? 'h-5 w-5' : 'h-6 w-6'}>
                    <AvatarFallback className="bg-forest text-white text-[10px]">
                      {record.userName.charAt(0)}
                    </AvatarFallback>
                    {record.userImageUrl && (
                      <AvatarImage src={record.userImageUrl} alt={record.userName} />
                    )}
                  </Avatar>
                  <div>
                    <p className={`font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>
                      {record.userName} &middot; {new Date(record.date).toLocaleDateString()} &middot; {record.teeBox} tees
                    </p>
                    <p className={`text-muted-foreground ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                      {scoreTypeLabel[record.scoreType] || record.scoreType}
                      {record.notes ? ` — ${record.notes.substring(0, 40)}${record.notes.length > 40 ? '...' : ''}` : ''}
                    </p>
                  </div>
                </div>
                {isOwn && (
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`px-1 h-7 ${isMobile ? 'w-6' : 'w-8'}`}
                      onClick={() => onEdit(toBirdieRecord(record, courseId, holeNumber))}
                      disabled={isLoading}
                    >
                      <Edit className={isMobile ? 'h-3 w-3' : 'h-4 w-4'} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`px-1 h-7 ${isMobile ? 'w-6' : 'w-8'} text-red-500 hover:text-red-700 hover:bg-red-50`}
                      onClick={() => onDelete(toBirdieRecord(record, courseId, holeNumber))}
                      disabled={isLoading}
                    >
                      <Trash2 className={isMobile ? 'h-3 w-3' : 'h-4 w-4'} />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GameBirdieHistory;
